<!-- User avatar with photo, initials fallback, and icon fallback. -->

<script lang="ts" setup>
import { User } from 'lucide-vue-next';
import { AvatarFallback, AvatarImage, AvatarRoot } from 'reka-ui';
import { computed } from 'vue';

import { getFullName } from '@sigma/utils/getFullName.js';
import { getInitials } from '@sigma/utils/getInitials.js';

export type AvatarColor = 'black' | 'blue' | 'grey' | 'white' | 'yellow';

interface Props {
  firstName?: string | null;
  lastName?: string | null;
  photo?: string | null;
  size?: number;
  color?: AvatarColor | null;
  /**
   * When true, render a shimmer skeleton instead of initials/icon.
   * Use this to indicate that the underlying user data is still loading.
   */
  loading?: boolean;
}

const props = defineProps<Props>();

const initials = computed(() => getInitials(props.firstName, props.lastName));
</script>

<template>
  <AvatarRoot
    :class="[css.avatarRoot, props.color && css[`color-${props.color}`]]"
    :style="{ '--avatar-size': props.size ? `${props.size}px` : undefined }"
  >
    <AvatarImage
      v-if="props.photo"
      :src="props.photo"
      :alt="getFullName(props.firstName, props.lastName)"
      :class="css.avatarImage"
    />
    <AvatarFallback :class="css.avatarFallback">
      <span
        v-if="props.loading"
        :class="css.skeleton"
      />
      <template v-else-if="initials">
        {{ initials }}
      </template>
      <User
        v-else
        :class="css.icon"
      />
    </AvatarFallback>
  </AvatarRoot>
</template>

<style lang="scss" module="css">
$avatar-size: 48px;

.avatarRoot {
  // Define component-specific CSS variables
  --avatar-background: #{$color-gray-50};
  --avatar-text-color: #{$color-gray-600};

  border-radius: 50%;
  background: var(--avatar-background);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  user-select: none;
  width: var(--avatar-size, $avatar-size);
  height: var(--avatar-size, $avatar-size);
  flex: 0 0 var(--avatar-size, $avatar-size);
  font-size: var(--avatar-size, $avatar-size);
}

// Color variants
.color {
  &-blue {
    --avatar-background: #{$color-doctor-100};
    --avatar-text-color: #{$color-doctor-700};
  }

  &-white {
    --avatar-background: #{$color-white};
    --avatar-text-color: #{$color-doctor-600};
  }

  &-yellow {
    // Lighter background than blue/grey so the dark yellow text clears the WCAG AA 4.5:1 ratio.
    --avatar-background: #{$color-yellow-25};
    --avatar-text-color: #{$color-yellow-700};
  }

  &-grey {
    --avatar-background: #{$color-gray-100};
    --avatar-text-color: #{$color-gray-600};
  }

  &-black {
    --avatar-background: #{$color-white};
    --avatar-text-color: #{$color-gray-900};
    border: 1px solid #{$color-gray-900};
  }
}

.avatarImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatarFallback {
  font-family: $font-generic;
  font-size: max(0.333em, $font-size-14);
  font-weight: $font-weight-400;
  color: var(--avatar-text-color);
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.icon {
  width: 60%;
  height: 60%;
  color: var(--avatar-text-color);
}

.skeleton {
  display: block;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    115deg,
    $color-gray-100 0%,
    $color-gray-100 25%,
    $color-gray-200 50%,
    $color-gray-100 75%,
    $color-gray-100 100%
  );
  background-size: 200% 200%;
  animation: avatar-shimmer 1.5s linear infinite;
}

@keyframes avatar-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}
</style>
